'use client';
import { useState } from 'react';
import Slider from 'react-slick';

import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';

import { Village } from '@/services/types';
import { cn } from '@/lib/utils';
import { buttonVariants } from '../Button';
import MapPinIcon from '../Icons/MapPin';

import { IRegionZoneDictonary } from '@/dictionaries/utils/region-zone/types';
import { IVillageDictionary } from '@/dictionaries/pages/village/types';

import MediaFilterImage from '@/components/Media/MediaFilterImage';
import { villagesMap } from '@/utils/maps';
import { useLocale } from '@/hooks/useLocale';

type VillageGalleryDicts = {
  regionZoneDict: IRegionZoneDictonary;
  villageDict: IVillageDictionary;
};

type Props = {
  handleClose: () => void;
  village: Village;
  dicts: VillageGalleryDicts;
};

const settings = {
  dots: false,
  infinite: true,
  speed: 1000,
  slidesToShow: 1,
  slidesToScroll: 1,
  arrows: false,
};

export default function VillageGallery({ handleClose, village, dicts }: Props) {
  const lang = useLocale();

  const { regionZoneDict, villageDict } = dicts;

  const [sliderRef, setSliderRef] = useState<Slider | null>(null);

  const medias = village.media.filter((media) => media.type === 'gallery');

  const villageZone = regionZoneDict[village.zone.code];

  const villageSubtitle = `${village.city} (${villageZone})`;

  return (
    <dialog className="block top-[0px] left-[0px] right-0 bottom-0 margin-0 fixed w-full h-full min-h-full bg-black/80 z-[999] p-4 overflow-y-auto">
      <button
        onClick={handleClose}
        className="absolute top-[1rem] right-[1rem] z-10 cursor-pointer appearance-none text-white">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-10 h-10 fill-current text-white"
          viewBox="0 -960 960 960">
          <path d="m256-168-88-88 224-224-224-224 88-88 224 224 224-224 88 88-224 224 224 224-88 88-224-224-224 224Z" />
        </svg>
      </button>
      <button
        onClick={sliderRef?.slickPrev}
        className="absolute top-[50%] cursor-pointer left-0 translate-y-[-50%] outline-none z-10">
        <FiChevronLeft className="w-20 h-20 text-white" />
      </button>
      <Slider
        ref={setSliderRef}
        className="top-[50%] translate-y-[-50%] h-[calc(100vh-40px)] py-[60px] px-[80px] [&_.slick-slide>div]:w-full"
        {...settings}>
        {medias.map((media, i) => {
          return (
            <div
              key={i}
              className="w-[1248px] min-h-[calc(100vh-160px)] bg-cover bg-[50%] !flex justify-between items-end flex-wrap p-[40px] relative bg-red">
              <MediaFilterImage
                media={media}
                filterName="large_gallery_wm_1280_webp"
                alt={village.title}
                width={100}
                height={100}
                className="absolute top-0 left-0 w-full max-w-full align-middle h-full object-cover"
                sizes="(min-width: 1280px) 80vw, (min-width: 767px) 90vw, (min-width: 480px) 100vw, 100vw"
              />
              <div className="w-[calc(100%+80px)] flex items-center m-[-40px] p-[30px] z-50 text-white">
                <div>
                  <h2 className="text-title font-title mb-2">
                    {village.title}
                  </h2>
                  <span className="text-xl flex items-center">
                    <MapPinIcon className="w-5 h-5 mr-1" />
                    {villageSubtitle}
                  </span>
                </div>
                <a
                  className={cn(
                    buttonVariants({
                      variant: 'default',
                      size: 'default',
                      className: 'ml-auto leading-[1.5] w-fit z-10',
                    }),
                  )}
                  href={`${villagesMap[lang]}/${village.slug}`}>
                  {villageDict.gallery.seeDetails}
                </a>
              </div>
            </div>
          );
        })}
      </Slider>
      <button
        onClick={sliderRef?.slickNext}
        className="absolute top-[50%] cursor-pointer right-0 translate-y-[-50%] outline-none z-10">
        <FiChevronRight className="w-20 h-20 text-white" />
      </button>
    </dialog>
  );
}
